<script setup>
import TableFilter from "./filters/TableFilter";
import Pagination from "./pagination/Pagination";
import NoDataTable from "./NoDataTable";
import {computed} from "vue";
import {useRoute} from "vue-router";
import {usePortalContentAndDesignStore} from "../../../stores/partners/portalContentAndDesignStore";
import {usePortalFilterAndPaginationStore} from "../../../stores/partners/filterAndPagination";
import EditableTextField from "../portal-editable-fields/EditableTextField.vue";

const route = useRoute();

const props = defineProps({
        showTableFilter: {
            type: Boolean,
            default: true
        },
        showPagination: {
            type: Boolean,
            default: true
        },
        paginationData: {
            type: Object,
            default: {}
        },
        filterableFields: {
            type: Object
        },
        loadingData: {
            type: Boolean,
            default: false
        }
})

const portalContentAndDesignStore = usePortalContentAndDesignStore();
const filterAndPaginationStore = usePortalFilterAndPaginationStore();

const layout = computed(() => portalContentAndDesignStore.portalLayoutSettings.portal_layout);
const layoutStyle = computed(() => portalContentAndDesignStore.portalLayoutSettings.portal_layout_layout_style);
const corners = computed(() => portalContentAndDesignStore.portalLayoutSettings.portal_layout_layout_corners);

const hasData = computed(() => {
    return props.paginationData?.data?.length > 0;
});

// Data has been loaded (pagination data exists with a data array)
const dataLoaded = computed(() => {
    return Array.isArray(props.paginationData?.data);
});

// Check if any filter is actively applied (search query or filter values)
const hasActiveFilters = computed(() => {
    // Check if search query is set
    if (filterAndPaginationStore.search && filterAndPaginationStore.search.trim() !== '') {
        return true;
    }

    // Default/empty filter values that should NOT be considered active filters
    const defaultFilterValues = [null, undefined, '', 'all', 'all_time'];

    // Check if any filter values are set (not empty/null/default)
    const filters = filterAndPaginationStore.filters;
    if (filters && typeof filters === 'object') {
        for (const key in filters) {
            const value = filters[key];
            // Consider a filter active if it has a non-default value
            if (!defaultFilterValues.includes(value)) {
                return true;
            }
        }
    }

    return false;
});

// Check if filters have been user-changed for this specific route
const filtersChangedForRoute = computed(() => {
    return filterAndPaginationStore.hasFiltersBeenChanged(route.name);
});

// Show placeholder when data is empty AND no filters are applied AND user hasn't changed filters on this route AND not loading
const showNoDataPlaceholder = computed(() => {
    return dataLoaded.value && !hasData.value && !hasActiveFilters.value && !filtersChangedForRoute.value && !props.loadingData;
});

// Show "No results" when data is empty AND (filters ARE applied OR user has changed filters on this route) AND not loading
const showNoResults = computed(() => {
    return dataLoaded.value && !hasData.value && (hasActiveFilters.value || filtersChangedForRoute.value) && !props.loadingData;
});

const getFlexClassesBasedOnLayoutOptions = () => {
    const classes = ['sm:h-24', 'h-auto'];

    if (layoutStyle.value === 'none') {
        classes.push('mb-4', corners.value);
        if (layout.value !== 'one') {
            classes.push('px-8', 'py-8', 'bg-gray-600', 'bg-opacity-5');
        }
    } else {
        classes.push('border-b', 'border-gray-200', 'border-opacity-75', 'px-6', 'sm:px-8');
    }

    return classes.join(' ');
};
</script>
<template>
    <template v-if="dataLoaded">
        <!-- Only show filter section when there's data OR when filters have been used on this route -->
        <div
            v-if="hasData || hasActiveFilters || filtersChangedForRoute"
            class="flex items-center py-6"
            :class="getFlexClassesBasedOnLayoutOptions()"
        >
            <table-filter
                v-if="showTableFilter"
                :filter-list="filterableFields"
            />
        </div>

        <div class="flex flex-col">
            <div class="overflow-x-auto">
                <template v-if="hasData">
                    <slot />
                </template>
                <!-- Show placeholder when no data AND no filters applied -->
                <no-data-table v-else-if="showNoDataPlaceholder" />
                <!-- Show "No results" only when filters are applied but return empty -->
                <template v-else-if="showNoResults">
                    <table :class="['table', layoutStyle === 'none' ? 'table-wide' : '']">
                        <tbody>
                            <tr>
                                <td colspan="10" class="text-center py-8 opacity-60">
                                    <editable-text-field
                                        text-type="span"
                                        v-model="portalContentAndDesignStore.translationSettings.portal_translation_general_no_results"
                                    >
                                        <template #custom-content>
                                            {{ portalContentAndDesignStore.translationSettings.portal_translation_general_no_results ?? 'No results' }}
                                        </template>
                                    </editable-text-field>
                                </td>
                            </tr>
                        </tbody>
                    </table>
                </template>
                <pagination
                    v-if="showPagination && hasData"
                    :pagination="paginationData"
                />
            </div>
        </div>
    </template>
</template>
